import { motion } from 'framer-motion';
import { TrophyIcon } from '@heroicons/react/24/solid';
import { TopLearner } from '../../types/analytics';
import { EASE_OUT } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

const RANK_COLORS = [
  'text-warning-500',
  'text-secondary-500',
  'text-primary-500',
  'text-grey-600',
  'text-success-500',
];

interface TopLearnersTodayProps {
  learners: TopLearner[];
  loading?: boolean;
}

function fullNameOf(learner: TopLearner): string {
  const name = `${learner.first_name ?? ''} ${learner.last_name ?? ''}`.trim();
  return name || 'Learner';
}

function LearnerItem({ learner, index }: { learner: TopLearner; index: number }) {
  const trophyColor = RANK_COLORS[index % RANK_COLORS.length];
  const fullName = fullNameOf(learner);

  return (
    <div className="flex items-center gap-4">
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-grey-200 dark:bg-grey-700 shrink-0 flex items-center justify-center text-sm font-semibold text-text-secondary">
        {fullName.charAt(0) || '?'}
      </div>

      {/* Name + today's best quiz result */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">{fullName}</p>
        <p className="text-xs text-grey-500 dark:text-grey-400 truncate">
          Got {learner.score_percent ?? 0}% on the &ldquo;{learner.course_title}&rdquo; quiz
        </p>
      </div>

      {/* Trophy icon */}
      <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-grey-800 flex items-center justify-center shrink-0">
        <TrophyIcon className={`w-5 h-5 ${trophyColor}`} />
      </div>
    </div>
  );
}

const PLACEHOLDER_LEARNERS: TopLearner[] = Array.from({ length: 3 }, (_, i) => ({
  id: `placeholder-${i}`,
  first_name: 'Loading',
  last_name: 'learner',
  score_percent: 0,
  course_title: 'Loading course',
}));

const TopLearnersToday: React.FC<TopLearnersTodayProps> = ({ learners, loading }) => {
  const { theme } = useTheme();
  // Drop any malformed rows the analytics endpoint may return without an id.
  const safeLearners = (learners ?? []).filter((l) => l && l.id);
  const displayLearners =
    loading && safeLearners.length === 0 ? PLACEHOLDER_LEARNERS : safeLearners;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.2, ease: EASE_OUT }}
      className="bg-white dark:bg-grey-900 rounded-2xl shadow overflow-hidden"
      data-testid="top-learners-today"
    >
      {/* Header */}
      <div className="px-6 pt-6 pb-2">
        <h2 className="text-lg font-semibold text-text-primary">Top Learners Today</h2>
      </div>

      {/* Learner list */}
      <div className="px-6 pb-6">
        <phantom-ui {...phantomProps(theme === 'dark', loading)}>
          {displayLearners.length > 0 ? (
            <div className="flex flex-col gap-6">
              {displayLearners.map((learner, index) => (
                <LearnerItem
                  key={learner.id ?? `row-${index}`}
                  learner={learner}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-text-tertiary text-center py-4">
              No learner activity today
            </p>
          )}
        </phantom-ui>
      </div>
    </motion.div>
  );
};

export default TopLearnersToday;
